import { isNumeric, sanitize, SanitizeMode } from "./Tools";
import { PlacementOptions, } from "./OptionResolver.types";
/**
 * OptionResolver is responsible for reading HTML attributes and user options
 * to build a complete ToggleOptions object.
 * It also handles deprecated options.
 */
var OptionResolver = /** @class */ (function () {
    function OptionResolver() {
    }
    /**
   * Gets a sanitized attribute value from an HTML element
   * @param element HTMLInputElement to read
   * @param attrName Attribute name
   * @param options method options
   * @param options.sanitized Flag to indicate if the sanitized mode needs to be used (default: `TEXT`)
   * @returns Sanitized attribute value or null
   */
    OptionResolver.getAttr = function (element, attrName, opts) {
        var _a = (opts !== null && opts !== void 0 ? opts : {}).sanitized, sanitized = _a === void 0 ? SanitizeMode.TEXT : _a;
        var value = element.getAttribute(attrName);
        return sanitize(value, { mode: sanitized });
        ;
    };
    /**
   * Returns the value of an attribute, user-provided value, or default value
   * @param element HTMLInputElement to read
   * @param attrName Attribute name
   * @param userValue Value provided by the user
   * @param defaultValue Default value if neither attribute nor user value exists
   * @param sanitized Flag to indicate if the sanitized mode needs to be used (default: `TEXT`)
   * @returns Final attribute value
   */
    OptionResolver.getAttrOrDefault = function (element, attrName, userValue, defaultValue, sanitized) {
        if (sanitized === void 0) { sanitized = SanitizeMode.TEXT; }
        var sanitizedUserValue = typeof userValue === "string" ? sanitize(userValue, { mode: sanitized }) : userValue;
        return OptionResolver.getAttr(element, attrName, { sanitized: sanitized }) ||
            sanitizedUserValue ||
            defaultValue;
    };
    /**
   * Returns the value of an attribute, user-provided value, or marks as deprecated
   * @param element HTMLInputElement to read
   * @param attrName Attribute name
   * @param userValue Value provided by the user
   * @param sanitized Flag to indicate if the sanitized mode needs to be used (default: `TEXT`)
   * @returns Final attribute value or DeprecationConfig.value if not found
   */
    OptionResolver.getAttrOrDeprecation = function (element, attrName, userValue, sanitized) {
        if (sanitized === void 0) { sanitized = SanitizeMode.TEXT; }
        var sanitizedUserValue = typeof userValue === "string" ? sanitize(userValue, { mode: sanitized }) : userValue;
        return OptionResolver.getAttr(element, attrName, { sanitized: sanitized }) ||
            sanitizedUserValue ||
            DeprecationConfig.value;
    };
    /**
   * Resolves all toggle options from the element and user options
   * @param element HTMLInputElement representing the toggle
   * @param userOptions Options provided by the user
   * @returns Complete ToggleOptions object
   */
    OptionResolver.resolve = function (element, userOptions) {
        var _a;
        if (userOptions === void 0) { userOptions = {}; }
        var options = {
            onlabel: this.getAttrOrDeprecation(element, "data-onlabel", userOptions.onlabel, SanitizeMode.HTML),
            offlabel: this.getAttrOrDeprecation(element, "data-offlabel", userOptions.offlabel, SanitizeMode.HTML),
            onstyle: this.getAttrOrDefault(element, "data-onstyle", userOptions.onstyle, OptionResolver.DEFAULT.onstyle),
            offstyle: this.getAttrOrDefault(element, "data-offstyle", userOptions.offstyle, OptionResolver.DEFAULT.offstyle),
            onvalue: this.getAttr(element, "value") || this.getAttrOrDefault(element, "data-onvalue", userOptions.onvalue, OptionResolver.DEFAULT.onvalue),
            offvalue: this.getAttrOrDefault(element, "data-offvalue", userOptions.offvalue, OptionResolver.DEFAULT.offvalue),
            ontitle: this.getAttrOrDefault(element, "data-ontitle", userOptions.ontitle, OptionResolver.getAttr(element, "title") ||
                OptionResolver.DEFAULT.ontitle),
            offtitle: this.getAttrOrDefault(element, "data-offtitle", userOptions.offtitle, OptionResolver.getAttr(element, "title") ||
                OptionResolver.DEFAULT.offtitle),
            size: this.getAttrOrDefault(element, "data-size", userOptions.size, this.DEFAULT.size),
            style: this.getAttrOrDefault(element, "data-style", userOptions.style, this.DEFAULT.style),
            width: this.getAttrOrDefault(element, "data-width", userOptions.width, this.DEFAULT.width),
            height: this.getAttrOrDefault(element, "data-height", userOptions.height, this.DEFAULT.height),
            tabindex: Number(this.getAttrOrDefault(element, "tabindex", userOptions.tabindex, this.DEFAULT.tabindex)),
            tristate: element.hasAttribute("tristate") ||
                userOptions.tristate ||
                OptionResolver.DEFAULT.tristate,
            name: this.getAttrOrDefault(element, "name", userOptions.name, this.DEFAULT.name),
            aria: {
                label: this.getAttrOrDefault(element, "aria-label", (_a = userOptions.aria) === null || _a === void 0 ? void 0 : _a.label, this.DEFAULT.aria.label),
            },
            tooltip: OptionResolver.resolveTooltipOptions(element, userOptions),
        };
        if (options.width && isNumeric(options.width))
            options.width = "".concat(options.width, "px");
        if (options.height && isNumeric(options.height))
            options.height = "".concat(options.height, "px");
        DeprecationConfig.handle(options, element, userOptions);
        return options;
    };
    /**
     * Resolve tooltip options from element attributes and user options.
     * @param element HTMLInputElement representing the toggle
     * @param userOptions Options provided by the user
     * @returns Resolved tooltip options or undefined if not found.
     */
    OptionResolver.resolveTooltipOptions = function (element, userOptions) {
        var _this = this;
        var _a, _b, _c, _d;
        var getTitle = function (attr, userOption) { return _this.getAttrOrDefault(element, attr, userOption, null, SanitizeMode.HTML) || _this.getAttr(element, "data-tooltip-title", { sanitized: SanitizeMode.HTML }); };
        var titleOn = getTitle("data-tooltip-title-on", (_a = userOptions.tooltip) === null || _a === void 0 ? void 0 : _a.title.on);
        var titleOff = getTitle("data-tooltip-title-off", (_b = userOptions.tooltip) === null || _b === void 0 ? void 0 : _b.title.off);
        var titleMixed = getTitle("data-tooltip-title-mixed", (_c = userOptions.tooltip) === null || _c === void 0 ? void 0 : _c.title.mixed);
        if (!titleOn || !titleOff)
            return OptionResolver.DEFAULT.tooltip;
        var placement = this.getAttrOrDefault(element, "data-tooltip-placement", (_d = userOptions.tooltip) === null || _d === void 0 ? void 0 : _d.placement, PlacementOptions.TOP);
        return {
            placement: Object.values(PlacementOptions).includes(placement) ? placement : PlacementOptions.TOP,
            title: {
                on: titleOn,
                off: titleOff,
                mixed: titleMixed !== null && titleMixed !== void 0 ? titleMixed : undefined,
            },
        };
    };
    /** Default values for all toggle options */
    OptionResolver.DEFAULT = {
        onlabel: "On",
        onstyle: "primary",
        onvalue: null,
        ontitle: null,
        offlabel: "Off",
        offstyle: "secondary",
        offvalue: null,
        offtitle: null,
        size: "",
        style: "",
        width: null,
        height: null,
        tabindex: 0,
        tristate: false,
        name: null,
        aria: { label: "Toggle", },
        tooltip: undefined,
    };
    return OptionResolver;
}());
export { OptionResolver };
/** Types of deprecation source */
var OptionType;
(function (OptionType) {
    OptionType["ATTRIBUTE"] = "attribute";
    OptionType["OPTION"] = "option";
})(OptionType || (OptionType = {}));
/**
 * Handles deprecated attributes and options for Bootstrap Toggle.
 */
var DeprecationConfig = /** @class */ (function () {
    function DeprecationConfig() {
    }
    /**
   * Processes deprecated options and attributes and logs warnings
   * @param options ToggleOptions object to update
   * @param element HTMLInputElement to read deprecated attributes from
   * @param userOptions UserOptions provided by the user
   */
    DeprecationConfig.handle = function (options, element, userOptions) {
        var _this = this;
        this.deprecatedOptions.forEach(function (_a) {
            var currentOpt = _a.currentOpt, deprecatedAttr = _a.deprecatedAttr, deprecatedOpt = _a.deprecatedOpt, mode = _a.mode;
            if (options[currentOpt] === DeprecationConfig.value) {
                var deprecatedAttrSanitized = sanitize(element.getAttribute(deprecatedAttr), { mode: mode });
                if (deprecatedAttrSanitized) {
                    _this.log(OptionType.ATTRIBUTE, deprecatedAttr, "data-".concat(currentOpt));
                    options[currentOpt] = deprecatedAttrSanitized;
                }
                else if (userOptions[deprecatedOpt]) {
                    _this.log(OptionType.OPTION, deprecatedOpt, currentOpt);
                    options[currentOpt] = userOptions[deprecatedOpt];
                }
                else {
                    options[currentOpt] = OptionResolver.DEFAULT[currentOpt];
                }
            }
        });
    };
    /**
   * Logs a deprecation warning to the console
   * @param type Source of the deprecated option (ATTRIBUTE | OPTION)
   * @param oldLabel Deprecated attribute or option name
   * @param newLabel Recommended replacement option name
   */
    DeprecationConfig.log = function (type, oldLabel, newLabel) {
        console.warn("Bootstrap Toggle deprecation warning: Using ".concat(oldLabel, " ").concat(type, " is deprecated. Use ").concat(newLabel, " instead."));
    };
    /** Unique string used to detect deprecated placeholders */
    DeprecationConfig.value = "BOOTSTRAP TOGGLE DEPRECATION CHECK -- a0Jhux0QySypjjs4tLtEo8xT2kx0AbYaq9K6mgNjWSs0HF0L8T8J0M0o3Kr7zkm7 --";
    /** Mapping of current option, deprecated attribute, and deprecated user option */
    DeprecationConfig.deprecatedOptions = [
        {
            currentOpt: "onlabel",
            deprecatedAttr: "data-on",
            deprecatedOpt: "on",
            mode: SanitizeMode.HTML
        },
        {
            currentOpt: "offlabel",
            deprecatedAttr: "data-off",
            deprecatedOpt: "off",
            mode: SanitizeMode.HTML
        },
    ];
    return DeprecationConfig;
}());
